import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from '@heroicons/react/24/outline';

import AssetsService from '../../services/AssetsService';
import { Station } from '../../services/models/assets';
import { getStationLiveStatus, hasFreshData, isVoltageOutOfRange } from '../../utils/stationStatus';
import useLocationRole from '../../hooks/useLocationRole';

const SiteOverview = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  // Linking a new station to the site is owner-only.
  const { canManage } = useLocationRole(siteId);

  const { data: stations } = useQuery({
    queryKey: ['stations', siteId],
    queryFn: () => AssetsService.getStations(Number(siteId)),
    enabled: !!siteId,
    select: (data) => data.stations,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });

  const getStationName = (station: Station) => {
    return station.name || `Station ${station.id}`;
  };

  const healthSummary = useMemo(() => {
    if (!stations) return null;
    let online = 0;
    let offline = 0;
    let inactive = 0;
    let lowBattery = 0;
    stations.forEach((station) => {
      const status = getStationLiveStatus(station);
      if (status === 'online') online += 1;
      else if (status === 'inactive') inactive += 1;
      else offline += 1;
      // Only flag battery when we have a fresh reading, regardless of active flag.
      if (hasFreshData(station) && isVoltageOutOfRange(station.voltage ?? 0)) lowBattery += 1;
    });
    return { total: stations.length, online, offline, inactive, lowBattery };
  }, [stations]);

  const renderStatusBadge = (station: Station) => {
    const status = getStationLiveStatus(station);
    if (status === 'online') {
      return (
        <div className="px-1.5 bg-success-100 rounded outline outline-1 outline-offset-[-1px] outline-success-500 flex justify-center items-center">
          <span className="text-success-600 typo-small">En ligne</span>
        </div>
      );
    }
    if (status === 'inactive') {
      return (
        <div className="px-1.5 bg-neutral-5 rounded outline outline-1 outline-offset-[-1px] outline-neutral-40 flex justify-center items-center">
          <span className="text-neutral-60 typo-small">Inactive</span>
        </div>
      );
    }
    return (
      <div className="px-1.5 bg-error-100 rounded outline outline-1 outline-offset-[-1px] outline-error-500 flex justify-center items-center">
        <span className="text-error-600 typo-small">Hors ligne</span>
      </div>
    );
  };

  const renderVoltageBadge = (station: Station) => {
    const voltage = station.voltage ?? 0;
    const fresh = hasFreshData(station);

    // No fresh reading — show unknown rather than a stale or misleading value.
    if (!fresh) {
      return (
        <div className="px-1.5 bg-neutral-5 rounded outline outline-1 outline-offset-[-1px] outline-neutral-40 flex justify-center items-center">
          <span className="text-neutral-60 typo-small">--</span>
        </div>
      );
    }
    if (isVoltageOutOfRange(voltage)) {
      return (
        <div className="px-1.5 bg-error-100 rounded outline outline-1 outline-offset-[-1px] outline-error-500 flex justify-center items-center">
          <span className="text-error-600 typo-small">{voltage.toFixed(1)}V</span>
        </div>
      );
    }
    return (
      <div className="px-1.5 bg-neutral-5 rounded outline outline-1 outline-offset-[-1px] outline-neutral-40 flex justify-center items-center">
        <span className="text-neutral-60 typo-small">{voltage.toFixed(1)}V</span>
      </div>
    );
  };

  return (
    <>
      <div className="w-full max-w-[1280px] px-4 flex flex-col justify-start items-start gap-8">
        <div className="self-stretch inline-flex justify-start items-start gap-4">
          <div className="flex-1 inline-flex flex-col justify-center items-start">
            <h1 className="text-neutral typo-h1">Vos stations</h1>
          </div>
        </div>
        {healthSummary && healthSummary.total > 0 && (
          <div className="self-stretch flex flex-wrap gap-3">
            <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
              <span className="text-neutral-60 typo-small">En ligne</span>
              <span className="text-success-600 typo-h2">{healthSummary.online}/{healthSummary.total}</span>
            </div>
            <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
              <span className="text-neutral-60 typo-small">Hors ligne</span>
              <span className={`typo-h2 ${healthSummary.offline > 0 ? 'text-error-600' : 'text-neutral'}`}>{healthSummary.offline}</span>
            </div>
            <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
              <span className="text-neutral-60 typo-small">Inactive</span>
              <span className={`typo-h2 ${healthSummary.inactive > 0 ? 'text-neutral-60' : 'text-neutral'}`}>{healthSummary.inactive}</span>
            </div>
            <div className="flex-1 min-w-[140px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 flex flex-col gap-1">
              <span className="text-neutral-60 typo-small">Batterie faible</span>
              <span className={`typo-h2 ${healthSummary.lowBattery > 0 ? 'text-error-600' : 'text-neutral'}`}>{healthSummary.lowBattery}</span>
            </div>
          </div>
        )}
      </div>
      <div className="w-full max-w-[1280px] px-4 flex flex-col justify-start items-center gap-4">
        <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap">
          {stations?.map((station) => (
            <button
              key={station.id}
              type="button"
              className="flex-1 h-36 max-w-96 min-w-[280px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 inline-flex flex-col justify-start items-start gap-2 overflow-hidden hover:shadow-md transition-shadow text-left"
              // A teammate has no overview tab, so send them straight to their
              // first usable one instead of bouncing through a redirect.
              onClick={() => navigate(
                canManage
                  ? `/site/${siteId}/station/${station.id}`
                  : `/site/${siteId}/station/${station.id}/operations`,
              )}
            >
              <div className="self-stretch inline-flex justify-start items-start gap-4">
                <div className="w-11 h-11 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10 inline-flex flex-col justify-center items-center overflow-hidden">
                  <img src="/station.svg" alt="" className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-1">
                  <span className="text-neutral typo-h3">
                    {getStationName(station)}
                  </span>
                  <div className="inline-flex justify-start items-start gap-1">
                    {renderStatusBadge(station)}
                    {renderVoltageBadge(station)}
                  </div>
                </div>
              </div>
            </button>
          ))}
          {canManage && (
            <button
              type="button"
              className="flex-1 h-36 max-w-96 min-w-[280px] p-4 rounded-lg border-2 border-dashed border-neutral-20 inline-flex flex-col justify-start items-start gap-2 overflow-hidden hover:border-neutral-40 hover:bg-neutral-5 transition-all text-left"
              onClick={() => navigate(`/site/${siteId}/station/new`)}
            >
              <div className="self-stretch inline-flex justify-start items-center gap-4">
                <div className="w-11 h-11 rounded-lg bg-neutral-5 inline-flex flex-col justify-center items-center overflow-hidden">
                  <PlusIcon className="w-4 h-4 text-neutral-40" />
                </div>
                <div className="flex-1 inline-flex flex-col justify-center items-start gap-1">
                  <span className="text-neutral-40 typo-h3">Ajouter une station</span>
                </div>
              </div>
            </button>
          )}
          {!canManage && (stations?.length ?? 0) === 0 && (
            <div className="flex-1 p-8 text-center typo-small text-neutral-40">
              Aucune station sur ce site pour le moment.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SiteOverview;
